import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { canManageAdminHierarchy, canManageTeam } from '../lib/permissions';

function prettyRole(role){
  if(role==='super_admin')return'Super Admin';
  if(role==='owner_admin')return'Owner / Admin';
  if(role==='setter')return'Setter';
  if(role==='closer')return'Closer';
  if(role==='viewer_plus')return'Viewer Plus';
  if(role==='viewer')return'Viewer';
  return role;
}

async function functionErrorMessage(error,data,fallback){
  if(data?.error)return data.error;
  try{
    const response=error?.context;
    if(response&&typeof response.clone==='function'){
      const payload=await response.clone().json();
      if(payload?.error)return payload.error;
    }
  }catch{}
  return error?.message||fallback;
}

function friendlyOtpError(message){
  if(/rate limit/i.test(message||'')){
    return 'Supabase email sending is temporarily rate-limited. No promotion was made. For reliable production OTP delivery, configure custom SMTP.';
  }
  return message||'Could not send the one-time code.';
}

const NON_ADMIN_ROLE_OPTIONS=[['setter','Setter'],['closer','Closer'],['viewer_plus','Viewer Plus'],['viewer','Viewer']];
const TEST_ELIGIBLE_ROLES=new Set(['owner_admin','setter','closer','viewer_plus','viewer']);
const SUPER_ADMIN_OPTION=['super_admin','Super Admin'];
const OWNER_ADMIN_OPTION=['owner_admin','Owner / Admin'];

export default function AdminTeamPage(){
  const {role,workspace,user}=useAuth();
  const [members,setMembers]=useState([]),[email,setEmail]=useState(''),[displayName,setDisplayName]=useState(''),[inviteRole,setInviteRole]=useState('setter'),[inviteAsTest,setInviteAsTest]=useState(false),[message,setMessage]=useState(''),[sending,setSending]=useState(false),[busy,setBusy]=useState('');
  const [otpTarget,setOtpTarget]=useState(null),[otpCode,setOtpCode]=useState(''),[otpError,setOtpError]=useState(''),[otpBusy,setOtpBusy]=useState(false),[otpChallengeId,setOtpChallengeId]=useState(''),[otpCooldownUntil,setOtpCooldownUntil]=useState(0),[otpClock,setOtpClock]=useState(Date.now());
  const canManageHierarchy=canManageAdminHierarchy(role);
  const inviteOptions=useMemo(()=>canManageHierarchy?[OWNER_ADMIN_OPTION,...NON_ADMIN_ROLE_OPTIONS]:NON_ADMIN_ROLE_OPTIONS,[canManageHierarchy]);
  const inviteRoleCanBeTest=TEST_ELIGIBLE_ROLES.has(inviteRole)&&(inviteRole!=='owner_admin'||canManageHierarchy);
  const otpCooldownSeconds=Math.max(0,Math.ceil((otpCooldownUntil-otpClock)/1000));

  async function loadMembers(){
    const {data,error}=await supabase.from('workspace_members')
      .select('workspace_id,user_id,role,active,is_test_account,invited_at,joined_at,profiles!workspace_members_user_id_fkey(email,display_name)')
      .eq('workspace_id',workspace.id).order('created_at',{ascending:true});
    if(error)setMessage(error.message);else setMembers(data??[]);
  }

  useEffect(()=>{if(canManageTeam(role))loadMembers()},[role,workspace.id]);
  useEffect(()=>{if(!inviteOptions.some(([v])=>v===inviteRole))setInviteRole(inviteOptions[0][0])},[inviteOptions,inviteRole]);
  useEffect(()=>{if(!inviteRoleCanBeTest&&inviteAsTest)setInviteAsTest(false)},[inviteRoleCanBeTest,inviteAsTest]);
  useEffect(()=>{
    if(!otpTarget||otpCooldownUntil<=Date.now())return undefined;
    const id=setInterval(()=>setOtpClock(Date.now()),1000);
    return()=>clearInterval(id);
  },[otpTarget,otpCooldownUntil]);

  if(!canManageTeam(role))return <Navigate to="/" replace/>;

  async function invite(e){
    e.preventDefault();setSending(true);setMessage('');
    const {data,error}=await supabase.functions.invoke('invite-user',{body:{workspaceId:workspace.id,email:email.trim().toLowerCase(),displayName:displayName.trim(),role:inviteRole,isTestAccount:inviteRoleCanBeTest&&inviteAsTest,redirectTo:`${window.location.origin}/auth/callback`}});
    setSending(false);
    if(error||data?.error){setMessage(await functionErrorMessage(error,data,'Invite failed.'));return;}
    const wasTest=inviteAsTest;
    setEmail('');setDisplayName('');setInviteAsTest(false);
    if(data?.reattached)setMessage(wasTest?'Existing account reattached as a test account.':'Existing account reattached to the workspace.');
    else setMessage(wasTest?'Test account invitation sent.':'Invitation sent.');
    loadMembers();
  }

  function closeOtp(){
    if(otpBusy)return;
    setOtpTarget(null);setOtpCode('');setOtpError('');setOtpChallengeId('');setOtpCooldownUntil(0);
  }

  async function beginSuperAdminPromotion(member){
    if(member.is_test_account){setMessage('Test accounts cannot be promoted to Super Admin. Unmark the account as test first.');return;}
    if(!canManageHierarchy){setMessage('Only Super Admin can promote another Super Admin.');return;}
    setOtpTarget(member);setOtpCode('');setOtpError('');setOtpChallengeId('');setOtpCooldownUntil(0);
  }

  async function sendPromotionOtp(){
    if(!otpTarget||otpBusy||otpCooldownSeconds>0)return;
    setOtpBusy(true);setOtpError('');
    const {data,error}=await supabase.functions.invoke('promote-super-admin',{body:{action:'begin',workspaceId:workspace.id,targetUserId:otpTarget.user_id}});
    setOtpBusy(false);
    if(error||data?.error){setOtpError(friendlyOtpError(await functionErrorMessage(error,data,'Could not send the one-time code.')));return;}
    setOtpChallengeId(data.challengeId||'');
    setOtpClock(Date.now());setOtpCooldownUntil(Date.now()+60000);
  }

  async function verifyOtpAndPromote(e){
    e.preventDefault();
    if(!otpTarget||!otpChallengeId||otpCode.trim().length<6)return;
    setOtpBusy(true);setOtpError('');
    const {data,error}=await supabase.functions.invoke('promote-super-admin',{body:{action:'complete',workspaceId:workspace.id,targetUserId:otpTarget.user_id,challengeId:otpChallengeId,otpCode:otpCode.trim()}});
    setOtpBusy(false);
    if(error||data?.error){setOtpError(await functionErrorMessage(error,data,'Super Admin promotion failed.'));return;}
    const identity=otpTarget.profiles?.display_name||otpTarget.profiles?.email||'Member';
    closeOtp();
    setMessage(`${identity} was promoted to Super Admin after challenge-bound OTP verification.`);
    loadMembers();
  }

  async function setRole(member,next){
    if(member.is_test_account&&next==='super_admin'){setMessage('Test accounts cannot be promoted to Super Admin. Unmark the account as test first.');return;}
    if(next==='super_admin'&&member.role!=='super_admin'){await beginSuperAdminPromotion(member);return;}
    setBusy(member.user_id);setMessage('');
    const {error}=await supabase.rpc('set_workspace_member_role',{p_workspace_id:workspace.id,p_user_id:member.user_id,p_role:next});
    setBusy('');if(error)setMessage(error.message);else loadMembers();
  }

  async function setActive(member,next){
    const verb=next?'reactivate':'deactivate';
    if(!confirm(`${verb[0].toUpperCase()+verb.slice(1)} ${member.profiles?.display_name||member.profiles?.email||'this user'}?`))return;
    setBusy(member.user_id);setMessage('');
    const {error}=await supabase.rpc('set_workspace_member_active',{p_workspace_id:workspace.id,p_user_id:member.user_id,p_active:next});
    setBusy('');if(error)setMessage(error.message);else loadMembers();
  }

  async function setTestAccount(member,next){
    const identity=member.profiles?.display_name||member.profiles?.email||'this user';
    setBusy(member.user_id);setMessage('');
    const {error}=await supabase.rpc('set_workspace_member_test_account',{p_workspace_id:workspace.id,p_user_id:member.user_id,p_is_test_account:next});
    setBusy('');
    if(error)setMessage(error.message);else{setMessage(next?`${identity} marked as a test account.`:`${identity} is no longer marked as a test account.`);loadMembers();}
  }

  async function removeMembership(member){
    const identity=member.profiles?.display_name||member.profiles?.email||'this user';
    if(!confirm(`Remove ${identity} from this workspace?\n\nThis removes CRM access while preserving historical attribution.`))return;
    setBusy(member.user_id);setMessage('');
    const {error}=await supabase.rpc('remove_workspace_member',{p_workspace_id:workspace.id,p_user_id:member.user_id});
    setBusy('');
    if(error)setMessage(error.message);else{setMessage(`${identity} was removed from the workspace.`);loadMembers();}
  }

  async function deleteTestUser(member){
    const identity=member.profiles?.display_name||member.profiles?.email||'this test user';
    setBusy(member.user_id);setMessage('');
    const {data:scan,error:scanError}=await supabase.functions.invoke('delete-test-user',{body:{action:'scan',workspaceId:workspace.id,targetUserId:member.user_id}});
    if(scanError||scan?.error){setBusy('');setMessage(await functionErrorMessage(scanError,scan,'Test-user impact scan failed.'));return;}
    const counts=scan?.impact?.counts||{};
    const summary=[`Permanently delete ${identity}'s Auth account?`,'',`Historical references preserved: ${scan?.impact?.totalHistoricalReferences??0}`,`Activity events: ${counts.activityEvents??0}`,`Audit/history events: ${counts.eventHistory??0}`,`Leads created: ${counts.leadsCreated??0}`,`Appointments created: ${counts.appointmentsCreated??0}`,`Payments recorded: ${counts.paymentsRecorded??0}`,'','The account will be removed while historical CRM attribution remains anonymized.'].join('\n');
    if(!confirm(summary)){setBusy('');return;}
    const confirmation=prompt(`Type DELETE to permanently delete ${identity}'s Auth account.`);
    if(confirmation!=='DELETE'){setBusy('');setMessage('Delete Test User cancelled.');return;}
    const {data,error}=await supabase.functions.invoke('delete-test-user',{body:{action:'delete',workspaceId:workspace.id,targetUserId:member.user_id,confirmation}});
    setBusy('');
    if(error||data?.error){setMessage(await functionErrorMessage(error,data,'Delete Test User failed.'));return;}
    setMessage(`${identity} was deleted. Historical CRM attribution was anonymized as Former test user.`);
    loadMembers();
  }

  return <section className="page-stack">
    <div><h1 className="page-title">Team</h1><p className="page-description">Invite and manage workspace access. Viewer Plus can see all non-Super-Admin surfaces without making changes; Viewer remains standard read-only CRM access. Test accounts cannot become Super Admin.</p></div>
    {message&&<div className="info-message">{message}</div>}
    <div className="admin-grid">
      <form className="panel" onSubmit={invite}>
        <h2>Invite user</h2>
        <label className="field-label">Name</label><input className="text-input" value={displayName} onChange={e=>setDisplayName(e.target.value)} required/>
        <label className="field-label">Email</label><input className="text-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required/>
        <label className="field-label">Role</label><select className="text-input" value={inviteRole} onChange={e=>setInviteRole(e.target.value)}>{inviteOptions.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>
        {inviteRoleCanBeTest&&<label className="test-account-option"><input type="checkbox" checked={inviteAsTest} onChange={e=>setInviteAsTest(e.target.checked)}/><span><strong>Test account</strong><small>Same role permissions; marks this account as disposable test access.</small></span></label>}
        <p className="small-note">Viewer Plus can inspect team, integrations, audit and settings but cannot change CRM or workspace data. Viewer can browse standard CRM data only. Only Super Admin can create or manage Owner/Admin test accounts. New users cannot be invited directly as Super Admin.</p>
        <button className="primary-button" disabled={sending}>{sending?'Sending…':'Send invitation'}</button>
      </form>
      <div className="panel team-panel">
        <div className="panel-head"><h2>Workspace members</h2><span className="pill">{members.filter(m=>m.active).length} active</span></div>
        {members.map(member=>{
          const adminTarget=['super_admin','owner_admin'].includes(member.role);
          const canEdit=canManageHierarchy||!adminTarget;
          const canMarkTest=canEdit&&member.user_id!==user.id&&TEST_ELIGIBLE_ROLES.has(member.role)&&(member.role!=='owner_admin'||canManageHierarchy);
          const canDeleteTest=member.is_test_account&&member.user_id!==user.id&&member.role!=='super_admin'&&(member.role!=='owner_admin'||canManageHierarchy);
          const roleOptions=canManageHierarchy?[OWNER_ADMIN_OPTION,...NON_ADMIN_ROLE_OPTIONS,...(member.is_test_account?[]:[SUPER_ADMIN_OPTION])]:NON_ADMIN_ROLE_OPTIONS;
          return <div className={`member-row member-control-row${member.active?'':' inactive'}`} key={member.user_id}>
            <div className="member-identity"><strong>{member.profiles?.display_name||member.profiles?.email||'Invited user'}{member.user_id===user.id?' · You':''}{member.is_test_account?' · Test':''}</strong><span>{member.profiles?.email||''}</span><small>{member.active?'Active':'Inactive'}{member.joined_at?' · Joined':' · Invited'}{member.is_test_account?' · Test account':''}</small></div>
            <div className="member-controls">
              {canEdit?<select className="compact-select" disabled={busy===member.user_id} value={member.role} onChange={e=>setRole(member,e.target.value)}>{roleOptions.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>:<span className="role-pill">{prettyRole(member.role)}</span>}
              {canMarkTest&&<button className="secondary-button inline-button" disabled={busy===member.user_id} onClick={()=>setTestAccount(member,!member.is_test_account)}>{member.is_test_account?'Unmark Test':'Mark as Test'}</button>}
              {canEdit&&member.user_id!==user.id&&<button className="secondary-button inline-button" disabled={busy===member.user_id} onClick={()=>setActive(member,!member.active)}>{member.active?'Deactivate':'Reactivate'}</button>}
              {canEdit&&member.user_id!==user.id&&<button className="secondary-button inline-button" style={{color:'#b42318',borderColor:'#f3b8b3'}} disabled={busy===member.user_id} onClick={()=>removeMembership(member)}>Remove Membership</button>}
              {canDeleteTest&&<button className="secondary-button inline-button" style={{color:'#8f1111',borderColor:'#e79a94',fontWeight:700}} disabled={busy===member.user_id} onClick={()=>deleteTestUser(member)}>{busy===member.user_id?'Working…':'Delete Test User'}</button>}
            </div>
          </div>;
        })}
      </div>
    </div>

    {otpTarget&&<div className="modal-backdrop" onMouseDown={e=>{if(e.target===e.currentTarget)closeOtp()}}>
      <form className="modal-card" onSubmit={verifyOtpAndPromote}>
        <div className="modal-head"><div><h2>Verify Super Admin promotion</h2><p>This OTP is bound to this member and can be used once.</p></div><button className="icon-button" type="button" onClick={closeOtp} disabled={otpBusy}>×</button></div>
        <div className="modal-body">
          <div className="info-message">Promoting <strong>{otpTarget.profiles?.display_name||otpTarget.profiles?.email||'this member'}</strong> to Super Admin.</div>
          {!otpChallengeId?<button type="button" className="secondary-button" onClick={sendPromotionOtp} disabled={otpBusy||otpCooldownSeconds>0}>{otpBusy?'Sending…':'Send promotion OTP'}</button>:<>
            <label className="field-label">One-time code</label>
            <input className="text-input" inputMode="numeric" autoComplete="one-time-code" value={otpCode} onChange={e=>setOtpCode(e.target.value.replace(/\D/g,'').slice(0,8))} placeholder="Enter code" disabled={otpBusy}/>
            <div className="success-message">OTP sent to {user.email}. This challenge expires in five minutes.</div>
            <button type="button" className="text-button" onClick={sendPromotionOtp} disabled={otpBusy||otpCooldownSeconds>0}>{otpCooldownSeconds>0?`Resend in ${otpCooldownSeconds}s`:'Send a new challenge'}</button>
          </>}
          {otpError&&<div className="error-message">{otpError}</div>}
        </div>
        <div className="modal-foot"><button type="button" className="secondary-button" onClick={closeOtp} disabled={otpBusy}>Cancel</button><button className="primary-button no-margin" disabled={otpBusy||!otpChallengeId||otpCode.trim().length<6}>{otpBusy?'Verifying…':'Verify & promote'}</button></div>
      </form>
    </div>}
  </section>;
}
